import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { FileTextIcon, UploadIcon, TrashIcon, DownloadIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, SearchIcon, FilterIcon, PlusIcon, FolderIcon, EyeIcon, ExternalLinkIcon } from 'lucide-react';
const Documents: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'verified' | 'pending' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Sample document data
  const documents = [{
    id: 'doc-1',
    name: 'Academic Transcript',
    type: 'PDF',
    status: 'verified',
    uploadDate: 'May 12, 2024',
    size: '1.2 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3753" />,
    applications: ['MBA - UAE Business School', 'MSc Finance - UAE University']
  }, {
    id: 'doc-2',
    name: 'Passport Copy',
    type: 'PDF',
    status: 'verified',
    uploadDate: 'May 10, 2024',
    size: '0.8 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3754" />,
    applications: ['MBA - UAE Business School', 'MSc Finance - UAE University']
  }, {
    id: 'doc-3',
    name: 'IELTS Certificate',
    type: 'PDF',
    status: 'pending',
    uploadDate: 'May 15, 2024',
    size: '0.5 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3755" />,
    applications: ['MBA - UAE Business School']
  }, {
    id: 'doc-4',
    name: 'CV/Resume',
    type: 'DOCX',
    status: 'rejected',
    uploadDate: 'May 14, 2024',
    size: '0.4 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3756" />,
    applications: ['MSc Finance - UAE University'],
    rejectionReason: 'Document is not properly formatted. Please use the provided template.'
  }, {
    id: 'doc-5',
    name: 'Recommendation Letter',
    type: 'PDF',
    status: 'verified',
    uploadDate: 'May 11, 2024',
    size: '0.6 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3757" />,
    applications: ['MBA - UAE Business School']
  }, {
    id: 'doc-6',
    name: 'Statement of Purpose',
    type: 'DOCX',
    status: 'pending',
    uploadDate: 'May 16, 2024',
    size: '0.3 MB',
    icon: <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-3758" />,
    applications: ['MSc Finance - UAE University']
  }];
  // Filter documents based on active tab and search query
  const filteredDocuments = documents.filter(doc => {
    const matchesTab = activeTab === 'all' || doc.status === activeTab;
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) || doc.type.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });
  // Get status badge styling
  const getStatusBadge = status => {
    switch (status) {
      case 'verified':
        return <span className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-50 text-green-700" data-id="element-3759">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3760" />
            Verified
          </span>;
      case 'pending':
        return <span className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700" data-id="element-3761">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3762" />
            Pending
          </span>;
      case 'rejected':
        return <span className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-50 text-red-700" data-id="element-3763">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-3764" />
            Rejected
          </span>;
      default:
        return null;
    }
  };
  return <Layout title="My Documents" persona="Student" data-id="element-3765">
      <div className="max-w-6xl mx-auto" data-id="element-3766">
        {/* Header with description */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6" data-id="element-3767">
          <div className="flex items-start" data-id="element-3768">
            <div className="flex-shrink-0 p-3 bg-primary-50 rounded-lg" data-id="element-3769">
              <FolderIcon className="w-6 h-6 text-primary-500" data-id="element-3770" />
            </div>
            <div className="ml-5" data-id="element-3771">
              <h2 className="text-xl font-medium text-gray-900 mb-2" data-id="element-3772">
                Document Management
              </h2>
              <p className="text-gray-600" data-id="element-3773">
                Upload and manage all your academic and personal documents in
                one place. These documents can be used across multiple program
                applications without needing to upload them again.
              </p>
            </div>
          </div>
        </div>
        {/* Main content */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3774">
          {/* Actions bar */}
          <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3775">
            <div className="relative" data-id="element-3776">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-3777" />
              <input type="text" placeholder="Search documents..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full md:w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3778" />
            </div>
            <div className="flex items-center space-x-2" data-id="element-3779">
              <button className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" data-id="element-3780">
                <UploadIcon className="w-4 h-4 mr-2" data-id="element-3781" />
                Upload New
              </button>
              <button className="p-2 text-gray-500 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3782">
                <FilterIcon className="w-5 h-5" data-id="element-3783" />
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200" data-id="element-3784">
            <div className="flex overflow-x-auto" data-id="element-3785">
              <button onClick={() => setActiveTab('all')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'all' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3786">
                All Documents ({documents.length})
              </button>
              <button onClick={() => setActiveTab('verified')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'verified' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3787">
                Verified (
                {documents.filter(d => d.status === 'verified').length})
              </button>
              <button onClick={() => setActiveTab('pending')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'pending' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3788">
                Pending Review (
                {documents.filter(d => d.status === 'pending').length})
              </button>
              <button onClick={() => setActiveTab('rejected')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'rejected' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3789">
                Rejected (
                {documents.filter(d => d.status === 'rejected').length})
              </button>
            </div>
          </div>
          {/* Document list */}
          <div className="p-4" data-id="element-3790">
            {filteredDocuments.length > 0 ? <div className="space-y-4" data-id="element-3791">
                {filteredDocuments.map(doc => <div key={doc.id} className="border border-gray-200 rounded-lg hover:border-primary-300 transition-colors" data-id="element-3792">
                    <div className="p-4" data-id="element-3793">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-3794">
                        <div className="flex items-center" data-id="element-3795">
                          <div className="p-2 bg-gray-100 rounded-md" data-id="element-3796">
                            {doc.icon}
                          </div>
                          <div className="ml-3" data-id="element-3797">
                            <h3 className="text-sm font-medium text-gray-800" data-id="element-3798">
                              {doc.name}
                            </h3>
                            <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-3799">
                              <span data-id="element-3800">{doc.type}</span>
                              <span className="mx-2" data-id="element-3801">•</span>
                              <span data-id="element-3802">{doc.size}</span>
                              <span className="mx-2" data-id="element-3803">•</span>
                              <span data-id="element-3804">Uploaded on {doc.uploadDate}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center mt-3 md:mt-0" data-id="element-3805">
                          {getStatusBadge(doc.status)}
                          <div className="flex ml-4" data-id="element-3806">
                            <button className="p-1.5 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" data-id="element-3807">
                              <EyeIcon className="w-4 h-4" data-id="element-3808" />
                            </button>
                            <button className="p-1.5 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors ml-1" data-id="element-3809">
                              <DownloadIcon className="w-4 h-4" data-id="element-3810" />
                            </button>
                            <button className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors ml-1" data-id="element-3811">
                              <TrashIcon className="w-4 h-4" data-id="element-3812" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Applications using this document */}
                      {doc.applications.length > 0 && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-3813">
                          <p className="text-xs text-gray-500 mb-2" data-id="element-3814">
                            Used in applications:
                          </p>
                          <div className="flex flex-wrap gap-2" data-id="element-3815">
                            {doc.applications.map((app, i) => <span key={i} className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs" data-id="element-3816">
                                {app}
                              </span>)}
                          </div>
                        </div>}
                      {/* Rejection reason */}
                      {doc.status === 'rejected' && doc.rejectionReason && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-3817">
                          <div className="bg-red-50 border border-red-100 rounded-md p-2 text-xs text-red-700" data-id="element-3818">
                            <span className="font-medium" data-id="element-3819">
                              Reason for rejection:
                            </span>{' '}
                            {doc.rejectionReason}
                          </div>
                        </div>}
                    </div>
                  </div>)}
              </div> : <div className="text-center py-12" data-id="element-3820">
                <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-3821">
                  <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-3822" />
                </div>
                <h3 className="text-gray-500 font-medium mb-2" data-id="element-3823">
                  No documents found
                </h3>
                <p className="text-gray-400 text-sm max-w-md mx-auto mb-6" data-id="element-3824">
                  {searchQuery ? 'Try using different search terms or filters' : 'Upload your first document to get started'}
                </p>
                {!searchQuery && <button className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" data-id="element-3825">
                    <UploadIcon className="w-4 h-4 mr-2" data-id="element-3826" />
                    Upload Document
                  </button>}
              </div>}
          </div>
        </div>
        {/* Document requirements info */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mt-6" data-id="element-3827">
          <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-3828">
            Document Requirements
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3829">
            <div className="border border-gray-200 rounded-md p-4" data-id="element-3830">
              <h4 className="font-medium text-gray-800 mb-2" data-id="element-3831">
                Academic Documents
              </h4>
              <ul className="text-sm text-gray-600 space-y-2" data-id="element-3832">
                <li className="flex items-start" data-id="element-3833">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3834" />
                  <span data-id="element-3835">
                    Official transcripts from all previous institutions
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3836">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3837" />
                  <span data-id="element-3838">Degree certificates (if applicable)</span>
                </li>
                <li className="flex items-start" data-id="element-3839">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3840" />
                  <span data-id="element-3841">
                    English proficiency test results (IELTS, TOEFL, etc.)
                  </span>
                </li>
              </ul>
            </div>
            <div className="border border-gray-200 rounded-md p-4" data-id="element-3842">
              <h4 className="font-medium text-gray-800 mb-2" data-id="element-3843">
                Personal Documents
              </h4>
              <ul className="text-sm text-gray-600 space-y-2" data-id="element-3844">
                <li className="flex items-start" data-id="element-3845">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3846" />
                  <span data-id="element-3847">Passport copy or national ID</span>
                </li>
                <li className="flex items-start" data-id="element-3848">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3849" />
                  <span data-id="element-3850">Professional CV/Resume</span>
                </li>
                <li className="flex items-start" data-id="element-3851">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2" data-id="element-3852" />
                  <span data-id="element-3853">Passport-sized digital photograph</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600" data-id="element-3854">
            <p data-id="element-3855">
              All documents should be uploaded in PDF format when possible.
              Maximum file size: 5MB per document.
            </p>
            <p className="mt-2" data-id="element-3856">
              Need help with document requirements?
              <a href="#" className="text-primary-600 hover:underline ml-1 inline-flex items-center" data-id="element-3857">
                View our documentation guide
                <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-3858" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>;
};
export default Documents;